import { ChangeDetectionStrategy, Component, OnInit, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { TextFieldModule } from '@angular/cdk/text-field';
import { MatButton } from '@angular/material/button';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import dayjs from 'dayjs';
import { EMPTY, Observable, map, switchMap } from 'rxjs';
import {
  DatePickerDialogComponent,
  DatePickerDialogData,
} from 'src/app/components/main/shared/date-picker-dialog/date-picker-dialog.component';
import { ArrayResponse } from 'src/app/models/array_response.interface';
import { DepartmentCategory } from 'src/app/models/department_category.model';
import { Role } from 'src/app/models/role.model';
import { User } from 'src/app/models/user.model';
import { WorkingContract } from 'src/app/models/working_contract.model';
import { WorkingContractType } from 'src/app/models/working_contract_type.model';
import { ApiService } from 'src/app/services/api.service';
import { PermissionsService } from 'src/app/services/permissions.service';
import { UtilsService } from 'src/app/services/utils.service';
import { WorkingContractVariableConditionListComponent } from '../working-contract-variable-condition-list/working-contract-variable-condition-list.component';

@Component({
  selector: 'app-user-detail-contract-section',
  templateUrl: './user-detail-contract-section.component.html',
  styleUrl: './user-detail-contract-section.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TextFieldModule,
    MatButton,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatFormField,
    MatIcon,
    MatInput,
    MatLabel,
    MatOption,
    MatSelect,
    MatSuffix,
    WorkingContractVariableConditionListComponent,
  ],
})
export class UserDetailContractSectionComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly dialog = inject(MatDialog);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly permissions = inject(PermissionsService);
  private readonly snack = inject(MatSnackBar);
  private readonly utils_service = inject(UtilsService);

  readonly active_contract = input.required<WorkingContract>();
  readonly user = input.required<User>();
  readonly its_me = input.required<boolean>();
  readonly contract_finalized = output<WorkingContract>();

  readonly form: UntypedFormGroup = this.fb.group({
    department_category_ids: ['', Validators.compose([Validators.required])],
    working_contract_type_id: ['', Validators.compose([Validators.required])],
    start_date: ['', Validators.compose([Validators.required])],
    end_date: ['', Validators.compose([])],
    job_description: ['', Validators.compose([Validators.required])],
    number_of_payments_per_year: ['', Validators.compose([Validators.required, Validators.min(12), Validators.max(14)])],
    price_per_hour: ['', Validators.compose([])],
    brute_anual_base_salary: ['', Validators.compose([Validators.required, Validators.min(14000)])],
    guardian_weekly_salary: ['', Validators.compose([])],
    guardian_trigger_attended_salary: ['', Validators.compose([])],
    guardian_price_per_hour: ['', Validators.compose([])],
    hours_per_week_expected: ['', Validators.compose([Validators.min(20), Validators.max(40), Validators.required])],
    flexible_remuneration_monthly_quantity: ['', Validators.compose([])],
    health_insurance_contract_id: ['', Validators.compose([])],
    possible_future_early_retirement: ['', Validators.compose([Validators.required])],
    possible_future_retirement_plan: ['', Validators.compose([Validators.required])],
    extract_flexible_remuneration_from_salary: ['', Validators.compose([Validators.required])],
  });
  readonly roles = toSignal(
    this.permissions.rolesObservable.pipe(
      map((roles_by_key: Record<string, Role> | null): Role[] =>
        roles_by_key == null ? [] : Object.keys(roles_by_key).map((role_key: string) => roles_by_key[role_key])
      )
    ),
    { initialValue: [] }
  );
  readonly department_categories = toSignal(
    this.api
      .getDepartmentCategories({ with: 'active_department_category_variable_condition' })
      .pipe(map((response: ArrayResponse<DepartmentCategory>): DepartmentCategory[] => response.data)),
    { initialValue: [] }
  );
  readonly working_contract_types = toSignal(
    this.api
      .getWorkingContractsTypes()
      .pipe(map((working_contract_types: WorkingContractType[]): WorkingContractType[] => working_contract_types)),
    { initialValue: [] }
  );

  ngOnInit(): void {
    this.form.disable();
    this.process_contract_data(this.active_contract());
  }

  download_contract(): void {
    const active_contract = this.active_contract();

    if (active_contract.current_document?.id) {
      this.download_document(active_contract.current_document.id);
    } else {
      this.snack.open('No se encontró documento de contrato de trabajo', 'Ok', { duration: 3000 });
    }
  }

  open_finalize_contract_dialog(): void {
    const data: DatePickerDialogData = {
      dialog_title: '¿En qué fecha finaliza el contrato?',
      dialog_ok_button: 'Finalizar',
      label: 'Fecha de finalización',
      filter: null,
      selected_date: dayjs().subtract(1, 'day').startOf('day').toDate(),
      min_date: dayjs(this.active_contract().start_date).startOf('day').toDate(),
    };

    this.dialog
      .open(DatePickerDialogComponent, {
        width: '400px',
        data,
      })
      .afterClosed()
      .pipe(
        switchMap((date: Date | undefined | null): Observable<WorkingContract> => {
          if (date == null) {
            return EMPTY;
          }

          const end_date = this.utils_service.normalizeMadridDateForApi(date, 'end');

          return this.api.finalizeWorkingContract(this.active_contract().id, { end_date });
        })
      )
      .subscribe({
        next: (updated_contract: WorkingContract): void => {
          this.form.controls['end_date'].patchValue(updated_contract.end_date, { emitEvent: false });
          this.snack.open('Contrato finalizado correctamente', 'Ok', { duration: 3000 });
          this.contract_finalized.emit(updated_contract);
        },
      });
  }

  private process_contract_data(working_contract: WorkingContract): void {
    const wcvc = working_contract.current_working_contract_variable_condition;

    this.form.patchValue({
      department_category_ids: wcvc.department_categories.map((department_category: DepartmentCategory): number => department_category.id),
      working_contract_type_id: working_contract.working_contract_type_id,
      start_date: working_contract.start_date,
      end_date: working_contract.end_date,
      job_description: working_contract.job_description,
      number_of_payments_per_year: working_contract.number_of_payments_per_year,
      price_per_hour: wcvc.price_per_hour,
      brute_anual_base_salary: wcvc.brute_anual_base_salary,
      guardian_weekly_salary: wcvc.guardian_weekly_salary ?? null,
      guardian_trigger_attended_salary: wcvc.guardian_trigger_attended_salary ?? null,
      guardian_price_per_hour: wcvc.guardian_price_per_hour ?? null,
      hours_per_week_expected: wcvc.hours_per_week_expected,
      flexible_remuneration_monthly_quantity: wcvc.flexible_remuneration_monthly_quantity ?? null,
      health_insurance_contract_id: wcvc.health_insurance_contract_id ?? null,
      possible_future_early_retirement: wcvc.possible_future_early_retirement,
      possible_future_retirement_plan: wcvc.possible_future_retirement_plan,
      extract_flexible_remuneration_from_salary: wcvc.extract_flexible_remuneration_from_salary,
    });
  }

  private download_document(document_id: number): void {
    this.api.downloadDocument(document_id).subscribe({
      next: (blob: Blob): void => {
        this.utils_service.downloadDocumentFromBlob(blob, `contrato-de-trabajo.pdf`);
      },
    });
  }
}
